/* eslint-disable @typescript-eslint/no-empty-function */
import _debounce from 'lodash/debounce';
import { isElementInView } from './_utils';
import triggerAnalyticsEvent from '../analytics/analytics';

const selectors = {
	video: '.Video-video',
	playBtn: '.Video-playButton',
};
const classes = {
	paused: 'Video--paused',
	playIcon: 'Button--playIcon--',
	pauseIcon: 'Button--pauseIcon--',
};

/**
 * Initializes a single video element, adding responsive source switching
 * @param  {HTMLMediaElement}  videoElement    The video element
 */
function initVideo(videoElement, initAutoPlay) {
	const listenedMedia = [];

	videoElement.addEventListener('canplay', () => {
		initAutoPlay();
	});
	videoElement.querySelectorAll('source').forEach(source => {
		if (
			source.media &&
			!listenedMedia.some(listened => listened.media.media === source.media)
		) {
			const mqListener = mq => {
				videoElement.load();
			};
			let mm = window.matchMedia(source.media);
			if (mm?.addListener) {
				mm.addListener(mqListener);
			} else {
				mm.addEventListener('change', mqListener);
			}
			listenedMedia.push({ media: mm, handler: mqListener });
		}
	});
}

/**
 * Plays or pauses the video given certain conditions
 * @param  {Element}  videoElement 		The HTML element of the video
 * @param  {[type]}  	paused       		True if the video is paused
 * @param  {[type]}  	userPaused   		True if the user has paused the video
 * @param  {[type]}  	windowActive 		True if the window for the page is currently active
 */
function conditionalPlayback(videoElement, userPaused, windowActive) {
	try {
		const paused = videoElement.paused;
		const isInView = isElementInView(videoElement, 50);

		if (isInView && !userPaused && windowActive) {
			if (paused) {
				videoElement
					.play()
					.then(() => {
						$(videoElement).show();
					})
					.catch(() => {
						$(videoElement).hide();
					});
			}
		} else {
			if (!paused) {
				videoElement.pause();
			}
		}
	} catch (e) {
		// Do nothing
	}
}

/**
 * Returns a function that toggles auto play for video
 * only when it gets visible on viewport
 * @param {Element} videoElement
 * @return {undefined}
 */
const getAutoPlay = videoElement => {
	return (windowActive = true) => {
		const videoSource = videoElement.currentSrc;
		if (videoSource && videoSource.length > 0) {
			const $videoElement = $(videoElement);
			const userPaused = $videoElement.hasClass(classes.paused);
			conditionalPlayback(videoElement, userPaused, windowActive);
		}
	};
};

/**
 * Initializes all the videos on the page and adds events to reinitialize/pause on
 * resize, scroll and window blur/focus.
 * @param  {String} [selector=selectors.video] The selector for all the video elements
 * @return {undefined}
 */
export default function initVideos(selector = selectors.video) {
	$(selector).each((index, element) => {
		const initAutoPlay = getAutoPlay(element);
		initVideo(element, initAutoPlay);

		// Setup the Play/Pause button
		$(element)
			.parent()
			.find(selectors.playBtn)
			.on('click', event => {
				event.stopPropagation();
				toggleVideoPlayback(event.target, element);
			});

		// Check auto play on scroll
		$(window).on('scroll.video', _debounce(initAutoPlay, 100));

		// Pause the video when the user leaves the tab/window.
		// This fixes a bug in Safari
		$(window).on('blur.video', () => {
			initAutoPlay(false);
		});

		// Play the video when the user returns to the tab/window.
		$(window).on('focus.video', () => {
			initAutoPlay(true);
		});
	});
}

/**
 * Toogles video playback and changes the state of the button
 * @param  {Element} buttonElement 	Button element for play/pause action
 * @param  {Element} videoElement  	Video element for which the action will be performed
 * @return {undefined}
 */
export const toggleVideoPlayback = (buttonElement, videoElement) => {
	try {
		const theme = $(buttonElement).data('theme');
		const $video = $(videoElement);
		if (videoElement.paused) {
			videoElement
				.play()
				.then(() => {
					$video.show();
				})
				.catch(() => {
					$video.hide();
				});
			triggerAnalyticsEvent('video.play', { element: videoElement });
		} else {
			videoElement.pause();
			triggerAnalyticsEvent('video.pause', { element: videoElement });
		}

		$video.toggleClass(classes.paused);
		$(buttonElement)
			.toggleClass(`${classes.playIcon}${theme}`)
			.toggleClass(`${classes.pauseIcon}${theme}`);
	} catch (e) {
		// Do nothing
	}
};
